import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const WizardContext = createContext(null);

const STORAGE_KEY = 'ph_draft_sports';

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
};

export const WizardProvider = ({ children, affiliateId, affiliateName, initialData }) => {
  // Check localStorage for a saved draft on mount
  const savedRaw = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  const saved = savedRaw ? JSON.parse(savedRaw) : null;
  const hasSaved = saved && saved.formData && saved.currentStep > 1 && !initialData;

  const [showResume, setShowResume] = useState(hasSaved);
  const [currentStep, setCurrentStep] = useState(1);
  const [draftId, setDraftId] = useState(saved?.draftId || '');
  const [formData, setFormData] = useState({
    affiliate_id: affiliateId,
    affiliate_name: affiliateName,
    first_name: '',
    last_name: '',
    applicant_email: '',
    applicant_phone: '',
    date_of_birth: '',
    age: null,
    org_name: '',
    street_address: '',
    unit: '',
    org_city: '',
    org_state: '',
    postal_code: '',
    num_participants_under_19: '',
    coverage_start_date: '',
    gl_limit_per_occurrence: '',
    gl_limit_aggregate: '',
    requested_optional_coverages: [],
    participant_accident_limit: '$25,000',
    claims_history_flag: false,
    claims_notes: '',
    claims_files: [],
    fraud_warranty_accepted: false,
    fraud_warranty_timestamp: null,
    additional_insured_required: false,
    additional_insured_details: '',
    risk_assessment_id: '',
    has_waiver: null,
    has_code_of_conduct: null,
    has_abuse_prevention_policy: null,
    has_background_checks: null,
    has_one_on_one_policy: null,
    ...(initialData || {}),
  });

  const totalSteps = 10;

  // Auto-save to localStorage on every change
  useEffect(() => {
    if (currentStep > 0 && !showResume) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        formData,
        currentStep,
        draftId,
        savedAt: new Date().toISOString(),
      }));
    }
  }, [formData, currentStep, draftId, showResume]);

  const resumeDraft = useCallback(() => {
    if (saved) {
      setFormData(prev => ({ ...prev, ...saved.formData }));
      setCurrentStep(saved.currentStep || 1);
      if (saved.draftId) setDraftId(saved.draftId);
    }
    setShowResume(false);
  }, [saved]);

  const discardDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setShowResume(false);
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const updateFormData = (data) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const goToStep = (step) => {
    if (step >= 1 && step <= totalSteps) setCurrentStep(step);
  };

  return (
    <WizardContext.Provider
      value={{
        currentStep,
        totalSteps,
        formData,
        updateFormData,
        nextStep,
        prevStep,
        goToStep,
        showResume,
        resumeDraft,
        discardDraft,
        clearDraft,
        draftId,
        setDraftId,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};
